/**
 * Safely normalizes a phone number.
 *
 * Rules:
 * - If it starts with "+" → return as-is
 * - If it starts with "0" → return as-is
 * - Otherwise → prepend "+"
 *
 * @param {any} value - Phone number input
 * @returns {string} Normalized phone number
 */
export function normalizePhoneNumber(value) {
  try {
    if (value === null || value === undefined) return "";

    // Convert to string safely and trim spaces
    const number = String(value).trim();

    if (!number) return "";

    // If already starts with "+" or "0", return as-is
    if (number.startsWith("+") || number.startsWith("0")) {
      return number;
    }

    // Otherwise add "+"
    return `+${number}`;
  } catch (err) {
    console.error("normalizePhoneNumber error:", err);
    return "";
  }
}

export function selfPhoneNumber(TeamsCallImpl) {
  if (!TeamsCallImpl || typeof TeamsCallImpl !== "object") return null;
  // for normalizing self phone number
  const phone = normalizePhoneNumber(TeamsCallImpl?._callAgent?._userProperties?.phoneNumber);
  return phone ?? null;
}

export const getPresenceStateLabel = async () => {
  try {
    // Session-based: credentials include session cookie, no need for Authorization header
    const response = await fetch("/api/getPresence", {
      method: "GET",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (response.status === 401) {
      console.error("getPresenceStateLabel: Session expired, please login again.");
      return {
        success: false,
        message: "Session expired. Please login again.",
        error: "SESSION_EXPIRED",
      };
    }

    const data = await response.json();

    if (response.ok && data?.success) {
      // Normalized success structure
      return {
        success: true,
        message: data.message,
        availability: data.availability,
        activity: data.activity,
        graph: data.graph,
      };
    } else {
      console.error("Error from getPresenceStateLabel:", data);
      return {
        success: false,
        message: data.message || "Graph call failed",
        error: data.error || "Unknown error",
      };
    }
  } catch (err) {
    console.error("getPresenceStateLabel exception:", err);
    return { success: false, message: "Exception: ", error: err.message };
  }
};

export const graphCallToGetNameOfResAcc = async (resAccId) => {
  let resAccName = "";
  try {
    const response = await fetch(`/api/getResAccInfo?resAccId=${encodeURIComponent(resAccId)}`, {
      method: "GET",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (response.status === 401) {
      console.error("graphCallToGetNameOfResAcc: Session expired, please login again.");
    } else if (response.ok) {
      const data = await response.json();
      resAccName = data?.graph?.displayName || "";
    } else {
      console.error("Failed to fetch resource account name:", response.status, response.statusText);
    }
  } catch (error) {
    console.error("Error fetching resource account name:", error);
  }
  return resAccName;
};

export const parseBooleanSetting = (val, fallback) => {
  if (typeof val === "boolean") {
    return val;
  }

  const normalized = String(val ?? "")
    .trim()
    .toLowerCase();
  if (["true", "1", "yes", "y", "on"].includes(normalized)) {
    return true;
  }
  if (["false", "0", "no", "n", "off"].includes(normalized)) {
    return false;
  }

  return fallback;
};
